/**
 * ProjectPropertyContext — single-query property provider for an entire project.
 *
 * Problem: many components independently called usePropertyManager({ projectId }),
 * each triggering a separate no-cache GraphQL query that loaded ALL project properties.
 * With N point clouds + annotation integrations + camera settings, this meant N+3 duplicate
 * full-property fetches on every project load.
 *
 * Solution: one <ProjectPropertyProvider> at the project root issues a single query.
 * All descendants call useProjectPropertyManager() to read from context instead.
 */

import React, { createContext, useContext, useEffect, useRef } from 'react';
import { usePropertyManager, UsePropertyManagerReturn } from './usePropertyManager';
import { PropertyType } from '../types';
import { getGlobalPropertyService, hasGlobalPropertyService } from '../reactive/globalPropertyService';

const ProjectPropertyContext = createContext<UsePropertyManagerReturn | null>(null);

interface ProjectPropertyProviderProps {
  projectId: string;
  organizationId?: string;
  children: React.ReactNode;
}

/**
 * Provides a single usePropertyManager instance for the entire project tree.
 * Mount once near the project root (e.g. in Project.tsx).
 */
export const ProjectPropertyProvider: React.FC<ProjectPropertyProviderProps> = ({ projectId, organizationId, children }) => {
  const propertyManager = usePropertyManager({ projectId, organizationId });

  // Bulk-preload all property values into the PropertyService once the GraphQL query completes.
  // This runs exactly once here (the provider never remounts) instead of in every
  // useReactivePropertyManager instance, which would re-run on each component mount
  // and overwrite runtime property changes with stale initial query data.
  const hasPreloaded = useRef(false);
  useEffect(() => {
    if (propertyManager.loading || hasPreloaded.current) return;
    if (!hasGlobalPropertyService()) return;

    const service = getGlobalPropertyService();
    const projectUserGroups = propertyManager.getProperties(PropertyType.PROJECT_USER);
    const projectGlobalGroups = propertyManager.getProperties(PropertyType.PROJECT_GLOBAL);
    const orgGroups = propertyManager.getProperties(PropertyType.ORGANIZATION);
    const userSettingsGroups = propertyManager.getProperties(PropertyType.USER_SETTINGS);

    // Order matters: PROJECT_GLOBAL (geometry/coords) must populate before
    // PROJECT_USER (toggles like `floor-isolation.<id>`) so subscribers
    // reacting to a preloaded toggle find valid coordinate data.
    if (projectGlobalGroups.length > 0) service.preloadValues(projectGlobalGroups, PropertyType.PROJECT_GLOBAL);
    if (projectUserGroups.length > 0) service.preloadValues(projectUserGroups, PropertyType.PROJECT_USER);
    if (orgGroups.length > 0) service.preloadValues(orgGroups, PropertyType.ORGANIZATION);
    if (userSettingsGroups.length > 0) service.preloadValues(userSettingsGroups, PropertyType.USER_SETTINGS);

    hasPreloaded.current = true;
  }, [propertyManager.loading, propertyManager]);

  return <ProjectPropertyContext.Provider value={propertyManager}>{children}</ProjectPropertyContext.Provider>;
};

/**
 * Consume the project-level property manager from context.
 * Must be used inside a <ProjectPropertyProvider>.
 */
export const useProjectPropertyManager = (): UsePropertyManagerReturn => {
  const ctx = useContext(ProjectPropertyContext);
  if (!ctx) {
    throw new Error('useProjectPropertyManager must be used within a <ProjectPropertyProvider>');
  }
  return ctx;
};

/**
 * Try to consume the project-level property manager from context.
 * Returns null when outside a <ProjectPropertyProvider> — callers can fall back to their own query.
 */
export const useOptionalProjectPropertyManager = (): UsePropertyManagerReturn | null => {
  return useContext(ProjectPropertyContext);
};
